import { externalMapLink } from "../../../geo/service";
// src/components/modules/Drivers/HistorialDispositivosDialog.js

import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Tag,
  Typography,
  Spin,
  Empty,
  Badge,
  Tooltip,
  Space,
} from "antd";
import {
  MobileOutlined,
  AppleOutlined,
  AndroidOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BellOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Text, Title } = Typography;

/**
 * Modal que muestra los dispositivos registrados de un conductor.
 * Permite ver el historial de dispositivos y su estado de notificaciones.
 */
const HistorialDispositivosDialog = ({ open, onClose, conductorId, conductorNombre }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && conductorId) {
      fetchDispositivos();
    }
  }, [open, conductorId]);

  const fetchDispositivos = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/conductores/${conductorId}/dispositivos`);
      setData(response.data);
    } catch (error) {
      console.error("Error al obtener dispositivos:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    if (platform === "ios") {
      return <AppleOutlined style={{ fontSize: 18, color: "#000" }} />;
    }
    return <AndroidOutlined style={{ fontSize: 18, color: "#3DDC84" }} />;
  };

  const columns = [
    {
      title: "Dispositivo",
      key: "dispositivo",
      render: (_, record) => (
        <Space>
          {getPlatformIcon(record.platform)}
          <div>
            <Text strong>{record.device_name || "Dispositivo desconocido"}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.device_brand} {record.device_model}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Sistema",
      key: "sistema",
      render: (_, record) => (
        <div>
          <Text>{record.os_version || "-"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            App v{record.app_version || "?"}
          </Text>
        </div>
      ),
    },
    {
      title: "Estado",
      key: "estado",
      align: "center",
      render: (_, record) => (
        <Space direction="vertical" size={4} align="center">
          <Tag
            color={record.is_active ? "green" : "default"}
            icon={
              record.is_active ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )
            }
          >
            {record.is_active ? "Activo" : "Inactivo"}
          </Tag>
          {record.has_fcm_token && (
            <Tooltip title="Puede recibir notificaciones push">
              <Tag color="blue" icon={<BellOutlined />}>
                Push
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Ubicacion Login",
      key: "location",
      align: "center",
      render: (_, record) => {
        if (record.login_latitude && record.login_longitude) {
          const mapsUrl = externalMapLink(record.login_latitude,record.login_longitude);
          return (
            <Tooltip title={`${record.login_latitude}, ${record.login_longitude}`}>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1890ff" }}
              >
                <EnvironmentOutlined style={{ fontSize: 18 }} />
              </a>
            </Tooltip>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Ultima Actividad",
      key: "last_activity",
      render: (_, record) => (
        <Tooltip
          title={
            record.last_activity_at
              ? dayjs(record.last_activity_at).format("DD/MM/YYYY HH:mm:ss")
              : "-"
          }
        >
          <Space>
            <ClockCircleOutlined />
            <Text>
              {record.last_activity_at
                ? dayjs(record.last_activity_at).fromNow()
                : "-"}
            </Text>
          </Space>
        </Tooltip>
      ),
    },
  ];

  const renderSummary = () => {
    if (!data) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <Space size="large">
          <Badge
            count={data.total_dispositivos}
            showZero
            style={{ backgroundColor: "#1890ff" }}
          >
            <Tag icon={<MobileOutlined />} style={{ padding: "4px 12px" }}>
              Total
            </Tag>
          </Badge>
          <Badge
            count={data.activos}
            showZero
            style={{ backgroundColor: "#52c41a" }}
          >
            <Tag icon={<CheckCircleOutlined />} style={{ padding: "4px 12px" }}>
              Activos
            </Tag>
          </Badge>
          <Badge
            count={data.con_notificaciones}
            showZero
            style={{ backgroundColor: "#722ed1" }}
          >
            <Tag icon={<BellOutlined />} style={{ padding: "4px 12px" }}>
              Con Push
            </Tag>
          </Badge>
        </Space>
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <MobileOutlined />
          <span>Dispositivos del Conductor</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {data?.conductor_nombre && (
        <Title level={5} style={{ marginBottom: 16 }}>
          {data.conductor_nombre}
        </Title>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" tip="Cargando dispositivos..." />
        </div>
      ) : data?.dispositivos?.length > 0 ? (
        <>
          {renderSummary()}
          <Table
            columns={columns}
            dataSource={data.dispositivos}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              Este conductor no tiene dispositivos registrados.
              <br />
              <Text type="secondary">
                Los dispositivos se registran automaticamente al iniciar sesion
                en la app.
              </Text>
            </span>
          }
        />
      )}
    </Modal>
  );
};

export default HistorialDispositivosDialog;
